import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../ui/table';
import { Trash2, Plus, XCircle, AlertTriangle } from 'lucide-react';
import { FieldLabel, ErrorText, SubSection, FlowArrow } from '../canFrameDecoder/RawFrameCard';
import IdBreakdown from './IdBreakdown';
import SpnRow, { StatusBadge } from './SpnRow';
import SpnBitMap from './SpnBitMap';
import { CATEGORY_LABELS, MAX_PAYLOAD, MAX_SPNS, parseHexBytes, spnColor } from '../../../lib/j1939FrameDecoder';

const J1939FrameCard = ({
  frame, index, result, spnResults, issues, onChange, onSpnChange, onAddSpn, onRemoveSpn, onRemove, canRemove,
}) => {
  const n = index + 1;
  const tid = `j19-frame-${n}`;
  const fe = result?.fieldErrors || {};
  const bytes = parseHexBytes(frame.data);
  const errorCount = issues.filter((i) => i.severity === 'error').length;
  const pgnText = result?.id ? `PGN ${result.id.pgn} (${result.id.pgnHex})` : frame.canId || '—';

  const field = (key, label, { tip, placeholder } = {}) => (
    <div className="space-y-2 min-w-0">
      <FieldLabel htmlFor={`${tid}-${key}`} tip={tip}>{label}</FieldLabel>
      <Input
        id={`${tid}-${key}`} value={frame[key]} placeholder={placeholder}
        onChange={(e) => onChange({ [key]: e.target.value })}
        className="font-mono" aria-invalid={!!fe[key]} data-testid={`${tid}-${key}-input`} spellCheck={false} autoComplete="off"
      />
      {fe[key] && <ErrorText testId={`${tid}-${key}-error`}>{fe[key]}</ErrorText>}
    </div>
  );

  const rows = frame.spns.map((s, k) => ({ spn: s, k, r: spnResults[s.id] }));

  return (
    <Card className="technical-card scroll-mt-24" id={`j1939-frame-${frame.id}`} data-testid={tid}>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="font-chivo uppercase tracking-tight text-base flex flex-wrap items-center gap-2">
            Frame {n}
            <span className="font-mono text-sm normal-case text-muted-foreground">{frame.messageName || 'Unnamed'} · {pgnText}</span>
          </CardTitle>
          <div className="flex items-center gap-2">
            {errorCount > 0 ? (
              <Badge variant="destructive" data-testid={`${tid}-issue-badge`}>{errorCount} issue{errorCount === 1 ? '' : 's'}</Badge>
            ) : result?.inDbc ? (
              <Badge variant="outline" className="text-emerald-700 border-emerald-600 dark:text-emerald-400">In DBC</Badge>
            ) : null}
            {canRemove && (
              <Button type="button" variant="ghost" size="sm" onClick={onRemove} aria-label={`Remove Frame ${n}`} data-testid={`${tid}-remove`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <SubSection title="1. J1939 Frame" testId={`${tid}-raw`}>
          {/* Explicit wrapper: the visual-edits Babel plugin breaks space-y-* on {children} in dev. */}
          <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {field('canId', 'CAN ID (29-bit hex)', { placeholder: '0x0CF00400', tip: 'Extended 29-bit identifier, 0x00000000 to 0x1FFFFFFF, with or without 0x. Frames with the same ID become one DBC message.' })}
            {field('messageName', 'Message Name', { placeholder: 'EEC1', tip: 'DBC message name: letters, digits and _, not starting with a digit.' })}
            {field('transmitter', 'Sender Node', {
              placeholder: result?.transmitterAuto && result?.transmitter ? `${result.transmitter} (auto)` : 'SA_0xNN (auto)',
              tip: 'DBC node (BU_) for the sender; its NmStationAddress is the source address. Blank = SA_0xNN from the ID. One name per source address.',
            })}
          </div>
          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <FieldLabel htmlFor={`${tid}-data`} tip="Hex bytes separated by spaces, commas or colons (F0 7D FF ...), or one contiguous hex string. 0 to 8 bytes.">Data (hex)</FieldLabel>
              <span
                className={`font-mono text-xs ${bytes !== null && bytes.length > MAX_PAYLOAD ? 'text-destructive' : 'text-muted-foreground'}`}
                data-testid={`${tid}-byte-count`}
              >
                {bytes === null ? 'not valid hex' : `${bytes.length} / ${MAX_PAYLOAD} bytes`}
              </span>
            </div>
            <Input
              id={`${tid}-data`} value={frame.data} onChange={(e) => onChange({ data: e.target.value })}
              className="font-mono" placeholder="F0 7D FF 00 1A 7D FF FF" spellCheck={false} autoComplete="off"
              aria-invalid={!!fe.data} data-testid={`${tid}-data-input`}
            />
            {fe.data && <ErrorText testId={`${tid}-data-error`}>{fe.data}</ErrorText>}
          </div>
          </div>
        </SubSection>

        <FlowArrow />

        <SubSection title="2. ID Breakdown" testId={`${tid}-id-section`}>
          <IdBreakdown breakdown={result?.id} testId={`${tid}-id`} />
        </SubSection>

        <FlowArrow />

        <SubSection title="3. SPNs" testId={`${tid}-spns`}>
          <div className="space-y-3">
            {rows.map(({ spn, k, r }) => (
              <SpnRow
                key={spn.id} spn={spn} k={k} result={r} tid={tid}
                onChange={(patch) => onSpnChange(spn.id, patch)}
                onRemove={() => onRemoveSpn(spn.id)}
                canRemove
              />
            ))}
            {rows.length === 0 && <p className="text-sm text-muted-foreground">No SPNs yet. Add one to decode the payload.</p>}
            <Button type="button" variant="outline" size="sm" onClick={onAddSpn} disabled={frame.spns.length >= MAX_SPNS} className="border-dashed" data-testid={`${tid}-add-spn`}>
              <Plus className="h-4 w-4" /> Add SPN
            </Button>
          </div>
        </SubSection>

        <FlowArrow />

        <SubSection title="4. Bit Map" testId={`${tid}-bits-section`}>
          <SpnBitMap
            spns={rows.map(({ spn, k, r }) => ({ k, name: spn.name, layout: r?.layout || null }))}
            dataBytes={result?.dataBytes ?? null}
            testId={`${tid}-bits`}
          />
        </SubSection>

        <FlowArrow />

        <SubSection title="5. Decoded Values" testId={`${tid}-summary`}>
          {rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing to decode.</p>
          ) : (
            <Table data-testid={`${tid}-summary-table`}>
              <TableHeader>
                <TableRow>
                  <TableHead className="px-2">SPN / Name</TableHead><TableHead className="hidden sm:table-cell px-2">Raw</TableHead>
                  <TableHead className="hidden sm:table-cell px-2">Status</TableHead><TableHead className="text-right px-2">Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(({ spn, k, r }) => {
                  const d = r?.decode;
                  return (
                    <TableRow key={spn.id}>
                      <TableCell className="px-2 font-mono max-w-[9rem] sm:max-w-none">
                        <div className="flex items-center whitespace-nowrap">
                          <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm mr-1.5" style={{ background: spnColor(k).bg }} />{spn.spn || '—'}
                        </div>
                        <div className="truncate text-xs text-muted-foreground" title={spn.name}>{spn.name || '—'}</div>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell px-2 font-mono whitespace-nowrap">{d?.decoded ? d.rawHex : '—'}</TableCell>
                      <TableCell className="hidden sm:table-cell px-2">{d ? <StatusBadge status={d.status} /> : '…'}</TableCell>
                      <TableCell className="px-2 text-right font-mono font-semibold" data-testid={`${tid}-summary-value-${k + 1}`}>
                        {d?.decoded && d.physical !== null ? `${d.physicalText}${d.unit ? ` ${d.unit}` : ''}` : '—'}
                        {d && <div className="sm:hidden mt-1 font-sans font-normal"><StatusBadge status={d.status} /></div>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </SubSection>

        {issues.length > 0 && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 space-y-1.5" data-testid={`${tid}-issues`}>
            <div className="font-mono text-[10px] uppercase tracking-wider text-destructive">DBC validation issues for this frame</div>
            {issues.map((i, k) => (
              <div key={k} className={`flex items-start gap-1.5 text-xs ${i.severity === 'error' ? 'text-destructive' : 'text-amber-700 dark:text-amber-400'}`}>
                {i.severity === 'error' ? <XCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> : <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />}
                <span><span className="font-semibold">{CATEGORY_LABELS[i.category] || i.category}:</span> {i.message}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default J1939FrameCard;
